import { CommonModule, NgClass, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { DocumentoMdService } from './documento-md.service';
import { Router } from '@angular/router';
import { NavigationService } from '@core/navigation/navigation.service';
import { firstValueFrom, Subject, takeUntil, combineLatest, filter, startWith } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { EmpresaService } from '@shared/service/app/empresa.service';
import { MarkdownModule, MarkdownService } from 'ngx-markdown';
import { NavigationEnd } from '@angular/router';

@Component({
    selector       : 'documento-md',
    templateUrl    : './documento-md.component.html',
    encapsulation  : ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs       : 'documento-md',
    standalone     : true,
    imports        : [CommonModule, MatButtonModule, MatIconModule],
    providers      : [DocumentoMdService]
})
export class DocumuentoMdComponent {

    @Input() rutaActual!: string;
    tieneDocumentacion: boolean = false;
    private _unsubscribeAll: Subject<void> = new Subject<void>();

    constructor(
        private navigationService: NavigationService,
        private documentoMdService: DocumentoMdService,
        private dialog: MatDialog,
        private router: Router,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        combineLatest([
            this.router.events.pipe(
                filter(event => event instanceof NavigationEnd),
                startWith(new NavigationEnd(0, this.router.url, this.router.url))
            ),
            this.navigationService.menuParsed$
        ])
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe(([event, menu]: [NavigationEnd, any[]]) => {
            if (menu && menu.length > 0) {
                this.verificarDocumentacion((event as NavigationEnd).urlAfterRedirects);
            }
        });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    async verificarDocumentacion(url: string) {
        const route = url.split('?')[0];
        const itemMenu = this.navigationService.getItemByRoute(route);

        if (itemMenu) {
            try {
                const resp = await firstValueFrom(
                    this.documentoMdService.getDocumentacionMd(itemMenu.iditem)
                );
                
                if (resp && Array.isArray(resp) && resp.length > 0) {
                    this.tieneDocumentacion = true;
                } else {
                    this.tieneDocumentacion = false;
                }
            } catch (error) {
                this.tieneDocumentacion = false;
            }
        } else {
            this.tieneDocumentacion = false;
        }
        
        this.cdr.markForCheck();
    }

    isVisible(): boolean {
        const currentUrl = this.router.url.split('?')[0];
        const excludedRoutes = [
            '/indicadores-de-venta'
        ];
        return !excludedRoutes.some(route => currentUrl.startsWith(route)) && this.tieneDocumentacion;
    }

    abrirDocumentacion(): void {
        const itemMenu = this.navigationService.getItemByRoute(this.rutaActual);

        if (itemMenu) {
            this.getDocumentoMD(itemMenu.iditem);
        } else {
            console.warn('No existe item de menú para esta ruta');
        }
    }

    async getDocumentoMD(iditem: number) {
        try {
            const resp = await firstValueFrom(
                this.documentoMdService.getDocumentacionMd(iditem)
            );

            if (!resp || (Array.isArray(resp) && resp.length === 0)) {
                this.mostrarSinDocumentacion();
                return;
            }

            const doc = resp[0];
            this.abrirDrawer(doc.descripcion, doc.contenido);

        } catch (error) {
            console.error('Error al obtener documentación:', error);
            this.mostrarSinDocumentacion();
        }
    }

    mostrarSinDocumentacion(): void {
        this.dialog.open(DialogoInfoComponent, {
            width: '400px',
            data: {
                titulo: 'Documentación no disponible',
                mensaje: `
                    No se encontró documentación asociada a este módulo.
                    <br>
                    Puede que aún no haya sido cargada.
                `
            }
        });
    }

    abrirDrawer(titulo: string, contenido: string): void {
        this.dialog.open(DocumentoMdViewerComponent, {
            width: '900px',
            height: '100vh',
            position: { right: '0' },
            panelClass: 'doc-drawer',
            data: { titulo, contenido }
        });
    }
}
@Component({
    standalone: true,
    imports: [CommonModule, MatButtonModule, MatDialogModule, MatIconModule],
    template: `
        <div class="flex flex-col items-center text-center px-6 pt-6">

            <!-- ICONO -->
            <div class="flex justify-center my-4">
                <mat-icon
                    class="w-11 h-11"
                    [svgIcon]="'heroicons_outline:information-circle'">
                </mat-icon>
            </div>

            <!-- TITULO -->
            <h2 class="text-xl font-semibold mb-2">
                {{ data.titulo }}
            </h2>

            <!-- MENSAJE -->
            <div
                class="text-gray-600 text-sm leading-relaxed mb-6"
                [innerHTML]="data.mensaje">
            </div>

        </div>

        <!-- ACCIONES -->
        <mat-dialog-actions align="center" class="pb-6">
            <button
                mat-raised-button
                mat-dialog-close
                class="w-full rounded-none md:w-36 md:rounded-lg py-8 md:py-0"
                [style.backgroundColor]="color_primario || ''"
            >
                <span class="text-white font-medium">Entendido</span>
            </button>
        </mat-dialog-actions>
    `
})
export class DialogoInfoComponent implements OnInit, OnDestroy {

    color_primario: string;
    color_secundario: string;
    private _unsubscribeAll = new Subject<void>();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        private _empresaService: EmpresaService
    ) {}

    ngOnInit(): void {
        this._empresaService.empresa$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(empresa => {
                this.color_primario = empresa.color_primario;
                this.color_secundario = empresa.color_secundario;
            });

        this._empresaService.getEmpresa();
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }
}
@Component({
    standalone: true,
    imports: [CommonModule, MarkdownModule, MatButtonModule, MatIconModule],
    providers: [MarkdownService],
    template: `
        <div class="flex flex-col h-full">

            <!-- Header -->
            <div class="flex items-center justify-between px-4 py-3 border-b">
                <h2 class="font-semibold text-lg">
                    {{ data.titulo }}
                </h2>

                <button mat-icon-button (click)="cerrar()">
                    <mat-icon>close</mat-icon>
                </button>
            </div>

            <!-- Contenido -->
            <div class="flex-1 overflow-auto p-6 prose max-w-none">
                <markdown [data]="data.contenido"></markdown>
            </div>

        </div>
    `
})
export class DocumentoMdViewerComponent {

    constructor(
        private dialogRef: MatDialogRef<DocumentoMdViewerComponent>,
        @Inject(MAT_DIALOG_DATA) public data: {
            titulo: string;
            contenido: string;
        }
    ) {}

    cerrar(): void {
        this.dialogRef.close();
    }
}